import { create } from 'zustand';
import type { PosPackageExpansion } from '../../api/packages';
import type { CartItem, Customer, Product } from './types';
import { PaymentMethod } from './types';

interface SalesState {
  // Cart
  cart: CartItem[];
  customer: Customer | null;
  paymentMethod: PaymentMethod;
  cashReceived: number;
  globalDiscount: number;

  // Computed-like
  getSubtotal: () => number;
  getVatAmount: () => number;
  getTotal: () => number;
  getChange: () => number;

  // Actions
  addToCart: (product: Product, quantity?: number) => void;
  addPackageToCart: (expansion: PosPackageExpansion) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  setItemDiscount: (productId: string, discount: number) => void;
  setCustomer: (customer: Customer | null) => void;
  setPaymentMethod: (method: PaymentMethod) => void;
  setCashReceived: (amount: number) => void;
  setGlobalDiscount: (discount: number) => void;
  clearCart: () => void;
  reset: () => void;
}

const calculateLineTotal = (product: Product, quantity: number, discount: number): number => {
  const basePrice = product.price * quantity;
  return Math.max(0, basePrice - discount);
};

export const useSalesStore = create<SalesState>((set, get) => ({
  cart: [],
  customer: null,
  paymentMethod: PaymentMethod.CASH,
  cashReceived: 0,
  globalDiscount: 0,

  getSubtotal: () => {
    return get().cart.reduce((sum, item) => sum + item.lineTotal, 0);
  },

  getVatAmount: () => {
    return get().cart.reduce((sum, item) => {
      const vatRate = item.product.vatRate / 100;
      const netPrice = item.lineTotal / (1 + vatRate);
      return sum + (item.lineTotal - netPrice);
    }, 0);
  },

  getTotal: () => {
    const subtotal = get().getSubtotal();
    const globalDiscount = get().globalDiscount;
    return Math.max(0, subtotal - globalDiscount);
  },

  getChange: () => {
    const total = get().getTotal();
    const cashReceived = get().cashReceived;
    return Math.max(0, cashReceived - total);
  },

  addToCart: (product, quantity = 1) => {
    set(state => {
      const existingIndex = state.cart.findIndex(item => item.product.id === product.id);

      if (existingIndex >= 0) {
        // Update quantity if already in cart — immutable update (bug-screen #4):
        // a régi item-objektumot NEM mutáljuk in-place (az identity-keyed memo/
        // re-render miatt stale lehetett), hanem új objektummal cseréljük.
        const existingItem = state.cart[existingIndex];
        if (!existingItem) return {};
        const newQuantity = existingItem.quantity + quantity;
        const updatedCart = state.cart.map((item, i) =>
          i === existingIndex
            ? {
                ...item,
                quantity: newQuantity,
                lineTotal: calculateLineTotal(product, newQuantity, item.discount),
              }
            : item
        );
        return { cart: updatedCart };
      }

      // Add new item
      const newItem: CartItem = {
        product,
        quantity,
        discount: 0,
        lineTotal: calculateLineTotal(product, quantity, 0),
      };
      return { cart: [...state.cart, newItem] };
    });
  },

  addPackageToCart: expansion => {
    set(state => {
      // a csomag-sor kulcsa a packageId (a pszeudo-product.id) — ismételt hozzáadás
      // a mennyiséget növeli (1 sor marad), mint az addToCart.
      const existingIndex = state.cart.findIndex(item => item.product.id === expansion.packageId);
      if (existingIndex >= 0) {
        const existingItem = state.cart[existingIndex];
        if (!existingItem) return {};
        const newQuantity = existingItem.quantity + 1;
        const updatedCart = state.cart.map((item, i) =>
          i === existingIndex
            ? {
                ...item,
                quantity: newQuantity,
                lineTotal: calculateLineTotal(item.product, newQuantity, item.discount),
              }
            : item
        );
        return { cart: updatedCart };
      }

      // a csomag 1 billing-sorként — pszeudo-product a csomag-adatokból (csomag-ár + ÁFA).
      // A pszeudo-product.stock irreleváns: a csomag-eladáskor a checkout-write CSAK a
      // packageId-t küldi, a BE a packageId-ből újra-expand-ál és atomian dekrementál (Q-T044).
      const pseudoProduct: Product = {
        id: expansion.packageId,
        sku: expansion.packageCode,
        name: expansion.packageName,
        category: 'CSOMAG',
        price: expansion.unitPrice,
        vatRate: expansion.taxRate,
        stock: 0,
        unit: 'csomag',
      };
      const newItem: CartItem = {
        product: pseudoProduct,
        quantity: 1,
        discount: 0,
        lineTotal: calculateLineTotal(pseudoProduct, 1, 0),
        // DISPLAY-célú (mit tartalmaz a csomag a kosárban) — a készlet-dekrementhez NEM
        // küldjük; a BE a packageId-ből autoritatív módon expand-ál+dekrementál (Q-T044).
        packageStockItems: expansion.stockItems,
        packageOriginalSum: expansion.originalSum ?? null,
      };
      return { cart: [...state.cart, newItem] };
    });
  },

  removeFromCart: productId => {
    set(state => ({
      cart: state.cart.filter(item => item.product.id !== productId),
    }));
  },

  updateQuantity: (productId, quantity) => {
    if (quantity <= 0) {
      get().removeFromCart(productId);
      return;
    }

    set(state => ({
      cart: state.cart.map(item =>
        item.product.id === productId
          ? {
              ...item,
              quantity,
              lineTotal: calculateLineTotal(item.product, quantity, item.discount),
            }
          : item
      ),
    }));
  },

  setItemDiscount: (productId, discount) => {
    set(state => ({
      cart: state.cart.map(item =>
        item.product.id === productId
          ? {
              ...item,
              discount,
              lineTotal: calculateLineTotal(item.product, item.quantity, discount),
            }
          : item
      ),
    }));
  },

  setCustomer: customer => set({ customer }),
  setPaymentMethod: paymentMethod => set({ paymentMethod }),
  setCashReceived: cashReceived => set({ cashReceived }),
  setGlobalDiscount: globalDiscount => set({ globalDiscount }),

  clearCart: () => set({ cart: [], globalDiscount: 0, cashReceived: 0 }),

  reset: () =>
    set({
      cart: [],
      customer: null,
      paymentMethod: PaymentMethod.CASH,
      cashReceived: 0,
      globalDiscount: 0,
    }),
}));
